// Tutorial page — public walkthrough of the product model (Task 4.2 / D-31 / D-36).
// SSR-safe: plain <a href> only, no AuthContext, no react-router.

import type { ReactNode } from "react";
import { MarketingLayout } from "../components/marketing/MarketingLayout.js";
import { MarketingShot } from "../components/marketing/MarketingShot.js";
import { Section } from "../components/marketing/Section.js";
import { CtaBand } from "../components/marketing/CtaBand.js";
import { AnchorButton } from "../components/ui/Button.js";
import { useSignupMode } from "../hooks/useSignupMode.js";
import { signupLabel, signupBandBody } from "../lib/signupCopy.js";

const SHOT_META: Record<string, { alt: string; width: number; height: number }> = {
  "path-a-create": {
    alt: "New checklist modal with the name Cabo, July 2026 and Packing selected",
    width: 1280,
    height: 800,
  },
  "path-a-items": {
    alt: "Checklist editor with four packing items ready to check off",
    width: 1280,
    height: 800,
  },
  "path-a-check": {
    alt: "Phone showing a mid-progress packing checklist",
    width: 780,
    height: 975,
  },
  "path-b-finish": {
    alt: "Phone showing Summer Family Campout checklist at about seventy percent complete",
    width: 780,
    height: 975,
  },
  "path-b-save": {
    alt: "Checklist overflow menu highlighting Save as template",
    width: 1280,
    height: 800,
  },
  "path-b-instantiate": {
    alt: "Templates page with Family Beach Trip and a Create checklist button",
    width: 1280,
    height: 800,
  },
  "path-c-lists": {
    alt: "My lists grid showing reusable Beach Day, Toiletries, and Carry-On lists",
    width: 1280,
    height: 800,
  },
  "path-c-template": {
    alt: "Template editor composing Beach Day, Toiletries, and Carry-On Essentials",
    width: 1280,
    height: 800,
  },
  "path-c-instantiate": {
    alt: "Templates page ready to instantiate Family Beach Trip into a fresh checklist",
    width: 1280,
    height: 800,
  },
  "sharing-sync": {
    alt: "Two phones showing live sync with attribution chips for You and Alex",
    width: 1280,
    height: 800,
  },
  "community-grid": {
    alt: "Community page grid of published lists including Carry-On Essentials and Beach Day",
    width: 1280,
    height: 800,
  },
  "ai-modal": {
    alt: "Create with AI modal showing a ski-trip packing result ready to save",
    width: 1280,
    height: 800,
  },
};

const NAV = [
  { href: "#the-model", label: "The model" },
  { href: "#path-a", label: "Start now" },
  { href: "#path-b", label: "Reuse" },
  { href: "#path-c", label: "Build properly" },
  { href: "#sharing", label: "Sharing" },
  { href: "#community", label: "Community" },
  { href: "#ai", label: "AI" },
  { href: "#recipes", label: "Recipes" },
  { href: "#tips", label: "Tips & limits" },
] as const;

type Tone = "surface" | "slate" | "amber";

const toneCls: Record<Tone, string> = {
  surface: "bg-white",
  slate: "bg-[#f8fafc]",
  amber: "bg-amber-50",
};

/** In-column section (no second max-w wrapper — lives inside the tutorial grid). */
function TutorialBlock({
  id,
  tone = "surface",
  children,
}: {
  id: string;
  tone?: Tone;
  children: ReactNode;
}) {
  return (
    <section id={id} className={`${toneCls[tone]} py-14 sm:py-20`}>
      {children}
    </section>
  );
}

function ScreenshotSlot({ label, slot }: { label: string; slot: string }) {
  const meta = SHOT_META[slot];
  if (!meta) {
    return (
      <div
        className="mt-4 flex aspect-[16/10] items-center justify-center rounded-2xl border border-dashed border-line bg-[#f8fafc] px-4 text-center text-sm text-muted"
        data-screenshot-slot={slot}
        aria-hidden="true"
      >
        Screenshot placeholder — {label}
      </div>
    );
  }
  return (
    <div className="mt-4" data-screenshot-slot={slot}>
      <MarketingShot
        name={slot}
        alt={meta.alt}
        width={meta.width}
        height={meta.height}
      />
    </div>
  );
}

function ModelDiagram() {
  return (
    <div
      className="mt-8"
      role="group"
      aria-label="How lists, templates, and checklists relate"
    >
      <div className="grid gap-4 md:grid-cols-3 md:items-stretch">
        <article className="rounded-2xl border border-line bg-white p-5 shadow-sm">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-brand-700">Lists</h3>
          <p className="mt-1 text-lg font-semibold text-ink">Building blocks</p>
          <ul className="mt-3 list-disc space-y-1 pl-5 text-sm leading-6 text-muted">
            <li>&ldquo;Swim bag&rdquo;</li>
            <li>&ldquo;Toiletries&rdquo;</li>
            <li>&ldquo;Kids&apos; stuff&rdquo;</li>
          </ul>
          <p className="mt-4 text-xs font-medium uppercase tracking-wide text-muted-sm">
            Not checkable
          </p>
        </article>

        <article className="rounded-2xl border border-line bg-white p-5 shadow-sm">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-brand-700">
            Templates
          </h3>
          <p className="mt-1 text-lg font-semibold text-ink">Blueprints</p>
          <ul className="mt-3 list-disc space-y-1 pl-5 text-sm leading-6 text-muted">
            <li>&ldquo;Beach trip&rdquo;</li>
            <li>= swim bag + toiletries + kids&apos; stuff</li>
          </ul>
          <p className="mt-4 text-xs font-medium uppercase tracking-wide text-muted-sm">
            Not checkable
          </p>
        </article>

        <article className="rounded-2xl border border-brand-200 bg-brand-50 p-5 shadow-sm">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-brand-700">
            Checklists
          </h3>
          <p className="mt-1 text-lg font-semibold text-ink">The real thing</p>
          <ul className="mt-3 list-none space-y-1 p-0 text-sm leading-6 text-muted">
            <li className="font-medium text-ink">&ldquo;Cabo, July 2026&rdquo;</li>
            <li>✓ goggles</li>
            <li>✓ sunscreen</li>
            <li>☐ passports</li>
          </ul>
          <p className="mt-4 text-xs font-medium uppercase tracking-wide text-brand-700">
            Checkable · shareable
          </p>
        </article>
      </div>

      <p className="mt-3 hidden text-center text-sm text-muted md:block" aria-hidden="true">
        Lists → Templates → Checklists
      </p>

      {/* The single most important line on the page (Task 4.2 / review §9). */}
      <p className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-center text-base font-semibold text-ink sm:text-lg">
        <span className="sr-only">Or skip lists and templates and </span>
        or skip both and start here
        <span className="mt-1 block text-sm font-normal text-muted">
          Go straight to a checklist — lists and templates are an upgrade, not a requirement.
        </span>
      </p>
    </div>
  );
}

function PathStep({
  n,
  title,
  body,
  slot,
}: {
  n: number;
  title: string;
  body: string;
  slot: string;
}) {
  return (
    <li className="grid gap-3 sm:grid-cols-[auto_1fr] sm:gap-6">
      <div
        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-600 text-sm font-bold text-white"
        aria-hidden="true"
      >
        {n}
      </div>
      <div className="min-w-0">
        <h3 className="text-lg font-semibold text-ink">
          <span className="sr-only">Step {n}. </span>
          {title}
        </h3>
        <p className="mt-1 max-w-[65ch] text-base leading-7 text-muted">{body}</p>
        <ScreenshotSlot label={title} slot={slot} />
      </div>
    </li>
  );
}

export function HowItWorksPage() {
  const { mode: signupMode } = useSignupMode();
  return (
    <MarketingLayout>
      <Section tone="surface">
        <h1 className="text-4xl font-bold leading-[1.05] tracking-tight text-ink sm:text-5xl lg:text-6xl">
          How NeatTick works
        </h1>
        <p className="mt-5 max-w-2xl text-lg leading-relaxed text-muted sm:text-xl">
          Three ideas — lists, templates, and checklists — and three ways in. Start with a
          checklist in about a minute, or build reusable pieces when you&apos;re ready.
        </p>
      </Section>

      <div className="border-t border-line">
        <div className="mx-auto max-w-6xl px-4 lg:grid lg:grid-cols-[13rem_minmax(0,1fr)] lg:gap-12">
          {/* Sticky in-page nav — lg+ only; plain stacked flow below */}
          <nav
            className="sticky top-20 hidden self-start py-10 lg:block"
            aria-label="On this page"
          >
            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-sm">
              On this page
            </p>
            <ul className="m-0 list-none space-y-1 p-0">
              {NAV.map((item) => (
                <li key={item.href}>
                  <a
                    href={item.href}
                    className="block rounded-lg px-2 py-1.5 text-sm text-muted no-underline hover:bg-slate-100 hover:text-ink"
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div className="min-w-0">
            {/* 1 — The model */}
            <TutorialBlock id="the-model" tone="slate">
              <h2 className="text-2xl font-semibold text-ink sm:text-3xl">
                The model in one glance
              </h2>
              <p className="mt-3 max-w-[65ch] text-base leading-7 text-muted">
                Lists are reusable building blocks. Templates snap them together into a blueprint.
                Checklists are the dated, checkable thing you actually use — and you can skip
                straight to them.
              </p>
              <ModelDiagram />
            </TutorialBlock>

            {/* 2 — Path A */}
            <TutorialBlock id="path-a" tone="surface">
              <h2 className="text-2xl font-semibold text-ink sm:text-3xl">
                Path A — I just need a checklist right now
              </h2>
              <p className="mt-3 max-w-[65ch] text-base leading-7 text-muted">
                The 60-second path. No lists or templates required.
              </p>
              <ol className="mt-8 list-none space-y-10 p-0">
                <PathStep
                  n={1}
                  title="Create a checklist"
                  body="From My Checklists, hit New checklist. Type a name — that's enough to start."
                  slot="path-a-create"
                />
                <PathStep
                  n={2}
                  title="Add items"
                  body="Type them one by one, paste a bullet list or CSV, or describe what you're doing and let AI build the first draft."
                  slot="path-a-items"
                />
                <PathStep
                  n={3}
                  title="Check things off on your phone"
                  body="Open the checklist as you go. Progress saves as you check. Share the link when you want help."
                  slot="path-a-check"
                />
              </ol>
              <p className="mt-8">
                <AnchorButton href="/register">{signupLabel("primary", signupMode)}</AnchorButton>
              </p>
            </TutorialBlock>

            {/* 3 — Path B */}
            <TutorialBlock id="path-b" tone="slate">
              <h2 className="text-2xl font-semibold text-ink sm:text-3xl">
                Path B — I do this regularly
              </h2>
              <p className="mt-3 max-w-[65ch] text-base leading-7 text-muted">
                The upgrade you take when you notice you&apos;re repeating yourself.
              </p>
              <ol className="mt-8 list-none space-y-10 p-0">
                <PathStep
                  n={1}
                  title="Finish a checklist once"
                  body="Do Path A for a trip, event, or process you've done before."
                  slot="path-b-finish"
                />
                <PathStep
                  n={2}
                  title="Save it as a template"
                  body='Open the checklist menu and choose "Save as template." Next time you create this whole checklist in one click.'
                  slot="path-b-save"
                />
                <PathStep
                  n={3}
                  title="Instantiate next time"
                  body="From Templates, create a fresh checklist. Everything starts unchecked — a clean run of the same plan."
                  slot="path-b-instantiate"
                />
              </ol>
            </TutorialBlock>

            {/* 4 — Path C */}
            <TutorialBlock id="path-c" tone="surface">
              <h2 className="text-2xl font-semibold text-ink sm:text-3xl">
                Path C — I want to build it properly
              </h2>
              <p className="mt-3 max-w-[65ch] text-base leading-7 text-muted">
                Optional. This is why the three tiers exist — for people who reuse pieces across
                many templates.
              </p>
              <ol className="mt-8 list-none space-y-10 p-0">
                <PathStep
                  n={1}
                  title="Create individual lists"
                  body={
                    'Build reusable blocks like "Toiletries," "Carry-on," or "Client kickoff tasks." Lists aren\'t checkable on their own.'
                  }
                  slot="path-c-lists"
                />
                <PathStep
                  n={2}
                  title="Compose a template"
                  body="Drop several lists into a template, add any one-off inline sections, and name the blueprint."
                  slot="path-c-template"
                />
                <PathStep
                  n={3}
                  title="Instantiate into a checklist"
                  body="Each run creates a snapshot. Editing a list later never changes checklists you've already started."
                  slot="path-c-instantiate"
                />
              </ol>
            </TutorialBlock>

            {/* 5 — Sharing */}
            <TutorialBlock id="sharing" tone="slate">
              <h2 className="text-2xl font-semibold text-ink sm:text-3xl">Sharing a checklist</h2>
              <div className="mt-4 max-w-[65ch] space-y-4 text-base leading-7 text-muted">
                <p>
                  Invite people with a link, or grant access by email. Choose{" "}
                  <strong className="font-semibold text-ink">view-only</strong> or{" "}
                  <strong className="font-semibold text-ink">can check items off</strong>.
                </p>
                <p>
                  When two or more people are on a checklist, checkmarks sync live — you see items
                  get checked the moment someone checks them, and an attribution chip shows who did
                  it.
                </p>
                <p>
                  You can remove someone anytime from the checklist&apos;s share settings. They lose
                  access immediately.
                </p>
              </div>
              <ScreenshotSlot label="live sync with attribution chips" slot="sharing-sync" />
            </TutorialBlock>

            {/* 6 — Community */}
            <TutorialBlock id="community" tone="surface">
              <h2 className="text-2xl font-semibold text-ink sm:text-3xl">Using the community</h2>
              <ol className="mt-6 list-decimal space-y-3 pl-5 text-base leading-7 text-muted">
                <li>Search or browse published lists.</li>
                <li>Open a list to preview every item.</li>
                <li>
                  Copy it into your workspace — your copy is yours. Editing it never changes the
                  original, and the publisher never sees your edits.
                </li>
                <li>
                  When you&apos;ve built something useful, publish one of your own lists so others
                  can find it.
                </li>
              </ol>
              <p className="mt-6">
                <a
                  href="/community"
                  className="text-base font-semibold text-brand-700 no-underline hover:text-brand-600"
                >
                  Browse community lists →
                </a>
              </p>
              <ScreenshotSlot label="community grid" slot="community-grid" />
            </TutorialBlock>

            {/* 7 — AI */}
            <TutorialBlock id="ai" tone="amber">
              <h2 className="text-2xl font-semibold text-ink sm:text-3xl">Generating with AI</h2>
              <p className="mt-3 max-w-[65ch] text-base leading-7 text-muted">
                Describe what you&apos;re doing and get a sensible first draft in seconds. Specific
                beats vague.
              </p>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <div className="rounded-2xl border border-line bg-white p-5">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-sm">
                    Weak prompt
                  </p>
                  <p className="mt-2 text-base text-ink">&ldquo;packing list&rdquo;</p>
                </div>
                <div className="rounded-2xl border border-brand-200 bg-white p-5">
                  <p className="text-xs font-semibold uppercase tracking-wide text-brand-700">
                    Strong prompt
                  </p>
                  <p className="mt-2 text-base text-ink">
                    &ldquo;Weekend ski trip with two toddlers, flying out Friday&rdquo;
                  </p>
                </div>
              </div>
              <p className="mt-6 max-w-[65ch] text-base leading-7 text-muted">
                Edit every item after it lands — AI is a starting point, not a final authority.
                There is a daily generation limit so the feature stays fast and fair for everyone.
              </p>
              <ScreenshotSlot label="AI generate modal with a result" slot="ai-modal" />
            </TutorialBlock>

            {/* 8 — Recipes */}
            <TutorialBlock id="recipes" tone="surface">
              <h2 className="text-2xl font-semibold text-ink sm:text-3xl">Five worked recipes</h2>
              <p className="mt-3 max-w-[65ch] text-base leading-7 text-muted">
                End-to-end patterns for common situations.
              </p>
              <div className="mt-8 space-y-6">
                <article className="rounded-2xl border border-line bg-white p-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-ink">Family beach trip</h3>
                  <p className="mt-2 max-w-[65ch] text-base leading-7 text-muted">
                    Three lists (Beach Day, Toiletries, Carry-On) → one template → one checklist per
                    trip, shared with a partner so packing syncs live.
                  </p>
                </article>
                <article className="rounded-2xl border border-line bg-white p-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-ink">New hire onboarding</h3>
                  <p className="mt-2 max-w-[65ch] text-base leading-7 text-muted">
                    A todo-kind template run per hire. Share the checklist with the new person so
                    they can see what&apos;s done and what&apos;s next.
                  </p>
                </article>
                <article className="rounded-2xl border border-line bg-white p-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-ink">Moving house</h3>
                  <p className="mt-2 max-w-[65ch] text-base leading-7 text-muted">
                    One big checklist, shared with everyone helping. Live sync does the real work —
                    no more &ldquo;did anyone call the movers?&rdquo;
                  </p>
                </article>
                <article className="rounded-2xl border border-line bg-white p-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-ink">Weekly house reset</h3>
                  <p className="mt-2 max-w-[65ch] text-base leading-7 text-muted">
                    A small template instantiated every Saturday. Same plan, fresh checkboxes each
                    week.
                  </p>
                </article>
                <article className="rounded-2xl border border-line bg-white p-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-ink">Event day-of run sheet</h3>
                  <p className="mt-2 max-w-[65ch] text-base leading-7 text-muted">
                    Inline sections for each phase of the day. Share read-only with the crew so
                    everyone follows the same script.
                  </p>
                </article>
              </div>
            </TutorialBlock>

            {/* 9 — Tips & limits */}
            <TutorialBlock id="tips" tone="slate">
              <h2 className="text-2xl font-semibold text-ink sm:text-3xl">Tips &amp; limits</h2>
              <ul className="mt-6 list-disc space-y-3 pl-5 text-base leading-7 text-muted">
                <li>
                  <strong className="font-semibold text-ink">Paste import:</strong> bullets,
                  numbered lines, and CSV-ish rows all parse into items. Blank lines become section
                  breaks where the importer supports them.
                </li>
                <li>
                  <strong className="font-semibold text-ink">Caps:</strong> lists and checklists top
                  out at 200 items; names and item text are capped at 200 characters.
                </li>
                <li>
                  <strong className="font-semibold text-ink">Snapshots:</strong> creating a checklist
                  freezes the items at that moment. Editing a source list or template never rewrites
                  checklists you&apos;ve already started.
                </li>
                <li>
                  <strong className="font-semibold text-ink">Deleting a referenced list:</strong>{" "}
                  templates that pointed at it keep working for checklists already created;
                  you&apos;ll need to fix the template before instantiating again.
                </li>
              </ul>
            </TutorialBlock>

            <div className="py-14 sm:py-20">
              <CtaBand
                title="Ready to try it?"
                body={signupBandBody(signupMode)}
                primaryHref="/register"
                primaryLabel={signupLabel("primary", signupMode)}
                secondaryHref="/community"
                secondaryLabel="Browse community lists"
              />
            </div>
          </div>
        </div>
      </div>
    </MarketingLayout>
  );
}
